import { useRef, type ReactNode } from 'react';
import { useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';

import { AnimationRemain } from '../animation/AnimationRemain';
import { Glyph } from '../common/Glyph';
import { Menu, MenuItem } from '../common/Menu';
import { Overlay } from '../overlay/Overlay';

import { suPx } from '../../theme/functions/su';
import { theme } from '../../theme/theme';
import styles from './TabAddMenu.module.css';

import { TabAddButton } from './TabAddButton';

export interface TabAddMenuItem {
  key: string;
  label: string;
  icon: string;
}

export interface TabAddMenuProps {
  ariaLabel: string;
  title: string;
  items: TabAddMenuItem[];
  onAction: (key: string) => void;
}

export function TabAddMenu(props: TabAddMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const triggerState = useOverlayTriggerState({ defaultOpen: false });
  const overlayTrigger = useOverlayTrigger({ type: 'menu' }, triggerState, triggerRef);

  function handleAction(key: string) {
    triggerState.close();
    props.onAction(String(key));
  }

  return (
    <>
      <TabAddButton
        {...overlayTrigger.triggerProps}
        ref={triggerRef}
        title={props.title}
        aria-label={props.title}
        onPress={triggerState.toggle}
      />

      <AnimationRemain condition={triggerState.isOpen} delay={200}>
        {(animationRemainProps: { isExiting: boolean; duration: string }) => (
          <Overlay
            targetRef={triggerRef}
            overlayProps={overlayTrigger.overlayProps}
            overlayTriggerState={triggerState}
            offset={suPx(1)}
            animationRemainProps={animationRemainProps}
            autoFocus={true}
            restoreFocus={true}
            shouldCloseOnOverlayOpen={true}
          >
            <div className={styles.root}>
              <Menu aria-label={props.ariaLabel} onAction={handleAction}>
                {props.items.map((item) => (
                  <MenuItem key={item.key} textValue={item.label}>
                    <TabAddMenuRow label={item.label} icon={item.icon} />
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </Overlay>
        )}
      </AnimationRemain>
    </>
  );
}

interface TabAddMenuRowProps {
  label: ReactNode;
  icon: string;
}

function TabAddMenuRow(props: TabAddMenuRowProps) {
  return (
    <div className={styles.item}>
      <Glyph source={props.icon} color={theme.icon.color_dark} size={theme.icon.size_default} />
      <span className={styles.itemLabel}>{props.label}</span>
    </div>
  );
}
